import { useCreate, useDelete, useUpdate } from '@refinedev/core';
import { useState } from 'react';
import { FileStatus, FileUploadInput, UploadedFile } from './types';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useUploadToStorage } from './useUploadToStorage';

export const useFileUploadActions = ({
  clientId: overrideClientId,
}: { clientId?: string } = {}) => {
  const { clientId: defaultClientId } = useCurrentClient();
  const clientId = overrideClientId ?? defaultClientId;
  const { mutateAsync: createFile } = useCreate<UploadedFile>();
  const { mutateAsync: updateFile } = useUpdate();
  const { mutateAsync: deleteFileMutation } = useDelete();
  const { uploadToStorage } = useUploadToStorage();
  const [error, setError] = useState<Error | null>(null);
  const resource = `clients/${clientId}/data-upload-files`;

  const uploadFiles = async (files: FileUploadInput[]) => {
    try {
      return Promise.all(
        files.map(async fileInput => {
          if (fileInput.file) {
            await uploadToStorage(fileInput.file, clientId);
          }

          return createFile({
            resource,
            values: {
              fileName: fileInput.fileName || fileInput.file?.name,
              fileSize: fileInput.file?.size || 0,
              fileType: fileInput.file?.type || 'FOLDER',
              isFolder: fileInput.isFolder || false,
              isResolved: fileInput.isResolved || false,
              isTemporary: fileInput.isTemporary || false,
              modelId: fileInput.modelId || undefined,
              parentId: fileInput.parentId,
            },
          });
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to upload files'));
      throw err;
    }
  };

  const deleteFile = async (fileId: string) => {
    try {
      await deleteFileMutation({
        resource,
        id: fileId,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete file'));
      throw err;
    }
  };

  const setFileResolved = async (file: UploadedFile, isResolved: boolean) => {
    try {
      await updateFile({
        resource,
        id: file.id,
        values: { ...file, isResolved },
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update file'));
      throw err;
    }
  };

  const moveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await updateFile({
        resource,
        id: file.id,
        values: { parentId, isResolved: file.isResolved, isFiled: true },
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to move file'));
      throw err;
    }
  };

  const createFolder = async (name: string, parentId?: string) => {
    try {
      await createFile({
        resource,
        values: {
          fileName: name,
          fileSize: 0,
          fileType: 'FOLDER',
          isFolder: true,
          isResolved: true,
          parentId,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create folder'));
      throw err;
    }
  };

  const renameFolder = async (file: UploadedFile, newName: string) => {
    try {
      await updateFile({
        resource,
        id: file.id,
        values: { ...file, fileName: newName, isResolved: file.isResolved },
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to rename folder'));
      throw err;
    }
  };

  const getFileStatus = (file: UploadedFile): FileStatus => {
    const status = file.backgroundProcess?.status;
    if (status === 'SUCCESS' || !status) {
      return { color: 'green.6', label: 'Ready' };
    } else if (status === 'FAILED') {
      return { color: 'red.6', label: 'Failed' };
    } else {
      return {
        color: 'blue.6',
        label: 'Processing',
        showLoader: true,
      };
    }
  };

  return {
    uploadFiles,
    deleteFile,
    setFileResolved,
    moveFile,
    createFolder,
    renameFolder,
    getFileStatus,
    error,
  };
};
